import { formPages, mergeRecipes, visiblePages, type FormFormat } from '@ayumia/component-runtime';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ConformityRules } from '../../work/form-targets';
import { TemplateForm } from '../../work/TemplateForm';
import { MediaRefProvider } from '../../work/form-v2/media-links';
import { SAMPLE_PERSON_ID } from '../../work/form-v2/PersonField';
import type { DraftStore } from '../../views/line-draft-store';
import type { CanvasNode, CanvasRenderProps, LibraryEntry, NodeActions } from '../editor/canvas-types';
import type { PreviewFormat } from '../editor/editor-types';
import { sampleInputs } from './forms-config';
import { blocksOf, pageOfSection, type Template } from './layout-model';
import { currentLayout, fr, recipesFor, type EditorRegistry } from './layout-registry';
import { CanvasEditing } from './canvas/CanvasEditing';
import { markCanvasOverrides } from './v2/presets';
import { usePreviewFocus, withPreviewFocus } from './preview-focus';

/**
 * Canevas du profil « formulaire » (ADR-146 §5.3, §5.4) : le rendu réel de l'écran de tâche
 * (`TemplateForm`, un seul runtime), rempli selon l'état d'aperçu choisi, dans le format choisi. Hors du
 * mode essai, un clic sélectionne l'élément (bloc, champ, partie) au lieu de le saisir. Rien n'est envoyé :
 * aucune tâche, aucune commande (ERP-PROJ-002) ; la validation d'essai ne fait que l'annoncer. Les gestes de
 * l'éditeur (sélection, glisser-déposer, navigation d'aperçu, édition sur place) sont dans `canvas/`.
 */

type Section = Template['sections'][number];
type Entry = Section['fields'][number];
type Answers = Record<string, unknown>;

export const FORM_STATES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'filled', label: 'Rempli' },
  { id: 'empty', label: 'Vide' },
  { id: 'errors', label: 'Erreurs' },
  { id: 'out_of_target', label: 'Hors cible' },
  { id: 'readonly', label: 'Lecture seule' },
];

/** Format de rendu de la fiche : tablette en paysage ou en portrait (selon la rotation), mobile, web. */
export function formFormatOf(format: PreviewFormat): FormFormat {
  if (format.id === 'tablet') return format.orientation === 'portrait' ? 'portrait' : 'landscape';
  if (format.id === 'mobile') return format.orientation === 'landscape' ? 'landscape' : 'mobile';
  return 'web';
}

const today = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/** Valeur d'exemple d'un champ ; `outOfTarget` pousse une mesure hors de sa cible. */
function sampleValue(entry: Entry, outOfTarget: boolean): unknown {
  const field = entry.field;
  if (entry.compute !== undefined) return undefined;
  const target = entry.target as { min?: unknown; max?: unknown } | undefined;
  switch (field.type) {
    case 'enumeration':
      return field.validation?.enum_values?.[0];
    case 'boolean':
      return true;
    case 'number':
    case 'decimal':
    case 'quantity': {
      const min = typeof target?.min === 'number' ? target.min : undefined;
      const max = typeof target?.max === 'number' ? target.max : undefined;
      if (outOfTarget && max !== undefined) return max + 5;
      if (outOfTarget && min !== undefined) return min - 5;
      if (min !== undefined && max !== undefined) return (min + max) / 2;
      return min ?? max ?? 3;
    }
    case 'date':
      return today();
    case 'reference':
      // Champ « Personne » (ADR-152) : la puce montre une personne d'exemple.
      return (entry.presentation as { widget?: string } | undefined)?.widget === 'person' ? SAMPLE_PERSON_ID : undefined;
    case 'string': {
      const pattern = field.validation?.pattern;
      return pattern !== undefined && /\[0-9\]/.test(pattern) ? '1234' : 'Exemple';
    }
    default:
      return undefined;
  }
}

/** Réponses d'exemple d'un état d'aperçu : rien pour « Vide » et « Erreurs », trois lignes par série. */
export function sampleAnswers(template: Template, state: string): Answers {
  if (state === 'empty' || state === 'errors') return {};
  const out = state === 'out_of_target';
  const answers: Answers = {};
  const fill = (entries: readonly Entry[], into: Answers): void => {
    for (const entry of entries) {
      const value = sampleValue(entry, out);
      if (value !== undefined) into[entry.field.field_id] = value;
    }
  };
  for (const section of template.sections) {
    if (section.repeat !== undefined) continue;
    if (section.series !== undefined) {
      answers[section.section_id] = [0, 1, 2].map(() => {
        const row: Answers = {};
        fill(section.fields, row);
        return row;
      });
    } else fill(section.fields, answers);
  }
  return answers;
}

/** Erreurs d'exemple : chaque champ obligatoire de la fiche, comme le serveur les renverrait. */
export function sampleErrors(template: Template): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const section of template.sections) {
    if (section.repeat !== undefined || section.series !== undefined) continue;
    for (const entry of section.fields) if (entry.field.required === true && entry.compute === undefined) errors[`answers.${entry.field.field_id}`] = 'Réponse obligatoire.';
  }
  return errors;
}

/** Brouillon fictif : il rend à `TemplateForm` les réponses de l'état choisi et la page à montrer. */
function previewStore(answers: Answers, pageId: string | undefined): DraftStore {
  return {
    load: () => ({ saved_at: '2026-01-01T00:00:00.000Z', values: { answers, ...(pageId === undefined ? {} : { page_id: pageId }) }, lines: [] }),
    save: () => undefined,
    clear: () => undefined,
  } as unknown as DraftStore;
}

/**
 * Page à montrer pour une sélection (page, partie, champ, bloc qui ne paraît que sur certaines pages) ;
 * `undefined` : la sélection ne demande pas de changer de page (formulaire, bloc présent partout).
 * Un bloc de plusieurs pages garde la page montrée s'il y paraît.
 */
export function pageOfSelection(template: Template, selected: string, current?: string): string | undefined {
  if (selected.startsWith('page:')) return selected.slice(5);
  if (selected.startsWith('section:')) return pageOfSection(template, selected.slice(8));
  if (selected.startsWith('field:')) {
    const index = Number(selected.split('/')[2]);
    const section = template.sections[index];
    return section === undefined ? undefined : pageOfSection(template, section.section_id);
  }
  if (selected.startsWith('block:')) {
    const block = blocksOf(template).find((candidate) => candidate.block_id === selected.slice(6));
    if (block?.page_ids === undefined) return undefined;
    return current !== undefined && block.page_ids.includes(current) ? current : block.page_ids[0];
  }
  return undefined;
}

export interface FormCanvasExtras {
  tree: (template: Template) => CanvasNode;
  actions: (template: Template, id: string) => NodeActions<Template>;
  library: (template: Template) => readonly LibraryEntry<Template>[];
}

/**
 * Canevas du formulaire : le rendu réel, et par-dessus, hors du mode essai, le calque d'édition. La page
 * montrée suit la sélection (calques, inspecteur) et la navigation d'aperçu (onglets, étapes, « Suivant ») ;
 * elle ne revient jamais d'elle-même à la première page.
 */
export function FormCanvas({ registry, props, rules = {}, extras }: { registry: EditorRegistry; props: CanvasRenderProps<Template>; rules?: ConformityRules; extras?: FormCanvasExtras }) {
  const { document: template, format, state, selected, onSelect, trying } = props;
  const layout = currentLayout(registry, template);
  const formFormat = formFormatOf(format);
  const recipe = useMemo(() => {
    const surface = formFormat === 'web' ? 'web' : 'operator';
    const merged = mergeRecipes(recipesFor(registry, layout).filter((candidate) => candidate.body.applies_to.surfaces.includes(surface)));
    return { ...(merged.density === undefined ? {} : { density: merged.density }), ...(merged.min_target_px === undefined ? {} : { min_target_px: merged.min_target_px }), hidden_regions: merged.hidden_regions };
  }, [registry, layout, formFormat]);
  // L'option choisie dans l'inspecteur (« Daim ») est celle que montre l'aperçu.
  const focus = usePreviewFocus();
  const answers = useMemo(() => withPreviewFocus(template, sampleAnswers(template, state), focus), [template, state, focus]);
  const inputs = useMemo(() => sampleInputs(template), [template]);
  const pages = useMemo(() => {
    const visible = visiblePages(template, answers, inputs);
    return visible.length > 0 ? visible : formPages(template).slice(0, 1);
  }, [template, answers, inputs]);
  // Page montrée : celle de la sélection quand elle en désigne une, sinon la dernière montrée.
  const [view, setView] = useState<{ selected: string; page: string | undefined }>(() => ({ selected, page: pageOfSelection(template, selected) }));
  if (view.selected !== selected) setView({ selected, page: pageOfSelection(template, selected, view.page) ?? view.page });
  const pageId = pages.some((page) => page.page_id === view.page) ? view.page : undefined;
  const layered = template.layout !== undefined;
  const declared = (template.layout?.pages?.length ?? 0) > 0;
  const navigate = (target: string): void => {
    const next = declared ? `page:${target}` : selected === '' ? '' : 'form';
    setView({ selected: next, page: target });
    if (next !== selected) onSelect(next);
  };
  const errors = useMemo(() => (state === 'errors' ? sampleErrors(template) : {}), [template, state]);
  // En édition, ce qu'une condition masque reste dessiné en pointillés, pour être choisi (addendum (15)).
  const reveal = !trying && props.showHidden !== false;
  const form = useMemo(
    () => (
      // Images de la médiathèque (Mes images, Communes) résolues pour l'aperçu, comme à la tâche (ADR-146 addendum (20)).
      <MediaRefProvider template={template}>
      <TemplateForm
        key={`${state}|${pageId ?? ''}|${format.id}|${layered ? 'layered' : 'plain'}|${trying ? 'try' : 'edit'}|${focus === null ? '' : `${focus.fieldId}=${focus.value}`}`}
        template={template}
        subject={undefined}
        disabled={state === 'readonly'}
        errors={errors}
        onSubmit={() => undefined}
        inputs={inputs}
        {...(layout === undefined || !layered ? {} : { layout: layout.body })}
        designRecipe={recipe}
        format={formFormat}
        sheetDate={today()}
        operator="Opérateur d’exemple"
        draftStore={previewStore(answers, pageId)}
        draftKey="studio-preview"
        onLeave={() => undefined}
        conformityRules={rules}
        preview
        revealHidden={reveal}
      />
      </MediaRefProvider>
    ),
    [template, state, pageId, format.id, layered, trying, errors, inputs, layout, recipe, formFormat, answers, rules, reveal],
  );
  const hostRef = useRef<HTMLDivElement>(null);
  // Étiquettes des zones (bouton « Zones ») sur les régions que le rendu réel pose déjà.
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (host === null || layout === undefined) return;
    for (const element of host.querySelectorAll('[data-region]')) {
      const region = layout.body.regions.find((candidate) => candidate.region_id === element.getAttribute('data-region'));
      if (region !== undefined) element.setAttribute('data-region-label', fr(region.label, ''));
    }
  });
  // Un geste du canevas sur un champ venu d'un préréglage déclare ses réglages changés « modifiés ici ».
  const editingProps: CanvasRenderProps<Template> = props.apply === undefined ? props : { ...props, apply: (next, coalesce) => props.apply!(markCanvasOverrides(template, next), coalesce) };
  if (extras === undefined) {
    return (
      <div ref={hostRef} className="ay-forms-canvas" data-zones={props.zones ? 'true' : undefined}>
        {form}
      </div>
    );
  }
  return (
    <div ref={hostRef} className="ay-forms-canvas__frame">
      <CanvasEditing registry={registry} props={editingProps} tree={extras.tree} actionsOf={extras.actions} library={extras.library} pages={pages} page={pageId ?? pages[0]?.page_id} onNavigate={navigate}>
        {form}
      </CanvasEditing>
    </div>
  );
}
